import {DeleteOutlined} from '@ant-design/icons';
import {Button, Col, Flex, Form, Row, Select, Spin, Table, Tag } from 'antd';
import {ColumnsType} from 'antd/es/table';
import {AdGroupBean, FilterListBean, FilterListLinkBean} from 'common/api/api';
import {getFilterListTypeName} from 'common/api/dictionaries'
import {filterListApi} from 'common/api/entitiesApi'
import {FilterListTypeSelector} from 'common/components/dictionary/FilterListTypeSelector'
import {useAppContext} from 'common/context/AppContext';
import {showError} from 'common/util/Util';
import {useEffect, useState} from 'react'
import TypeEnum = FilterListBean.TypeEnum
import FilterListModeEnum = FilterListLinkBean.FilterListModeEnum
import { FilterListModeSelector } from 'common/components/dictionary/FilterListModeSelector.tsx'

export interface Item {
    id: number
    name: string
    processed?: boolean
    type: TypeEnum
    mode: FilterListModeEnum
}

interface Props {
    entity: AdGroupBean,
    setEntity: (entity: AdGroupBean) => void
}

const FilterListCard = ({entity, setEntity}: Props) => {

    const appContext = useAppContext()
    const [loading, setLoading] = useState(false)

    const [filterListType, setFilterListType] = useState<TypeEnum | undefined>(undefined)
    const [filterListAll, setFilterListAll] = useState<Array<FilterListBean>>([])
    const [filterListOptions, setFilterListOptions] = useState<any | undefined>([])
    const [filterListMode, setFilterListMode] = useState<FilterListModeEnum | undefined>(undefined)
    const [filterListSelected, setFilterListSelected] = useState<Item[]>([])
    const [filterListSelectKey, setFilterListSelectKey] = useState(0)

    useEffect(() => {
        setLoading(true)
        filterListApi.getPageFilterLists('name', 'asc')
            .then(filterListItems => {
                const filterLists = (filterListItems.data || [])
                    .flatMap(item => item.data ? [item.data] : [])
                setFilterListAll(filterLists)
            })
            .catch(e => showError(appContext, e))
            .finally(() => setLoading(false))
    }, []);

    useEffect(() => {
        updateFilterListSelected(entity.filterListLinks || [], filterListAll)
    }, [entity.filterListLinks]);


    const changeFilterListType = (type: TypeEnum) => {
        setFilterListType(type)
        updateFilterListOptions(type, entity.filterListLinks || [], filterListAll)
    }

    const unlink = (id) => {
        const filterListLinks = entity.filterListLinks && entity.filterListLinks.filter(filterList => filterList.filterListId !== id)
        setEntity({...entity, filterListLinks})

        updateFilterListOptions(filterListType, filterListLinks || [], filterListAll)
    }

    const link = (filterListId: number) => {
        const filterListLinks: FilterListLinkBean[] = [] // we have to create new array, because we watch change of the entity.filterListLinks field
        filterListLinks.push(...(entity.filterListLinks || []))
        filterListLinks.push({
            filterListId: filterListId,
            filterListMode: filterListMode
        })
        setEntity({...entity, filterListLinks})

        updateFilterListOptions(filterListType, filterListLinks, filterListAll)
    }

    function updateFilterListSelected(filterListLinks: Array<FilterListLinkBean>, filterListAll: Array<FilterListBean>) {
        setFilterListSelected(filterListLinks
            .map(filterListLink => {
                const filterListBean = filterListAll.find(filterList => filterList.id == filterListLink.filterListId)
                return (filterListBean ? {
                    id: filterListBean.id,
                    name: filterListBean.name,
                    type: filterListBean.type,
                    processed: filterListBean.processed,
                    mode: filterListLink.filterListMode
                } : {}) as Item
            })
            .filter(bean => bean.id)
        )
    }

    function updateFilterListOptions(type: TypeEnum | undefined, filterListLinks: Array<FilterListLinkBean>, filterListAll: Array<FilterListBean>) {
        setFilterListOptions(filterListAll
            .filter(filterList =>
                filterList.type === type &&
                !filterListLinks.find(filterListLink => filterListLink.filterListId === filterList.id)
            )
            .map(f => ({value: f.id, label: f.name}))
        )
    }

    const filterListColumns: ColumnsType<any> = [
        {
            title: 'Type',
            key: 'type',
            dataIndex: 'type',
            width: 100,
            render: (value) => getFilterListTypeName(value)
        },
        {
            title: <span style={{whiteSpace: 'nowrap'}}>Linked List</span>,
            key: 'name',
            dataIndex: 'name',
            width: 220,
            render: (value) => <span style={{overflowWrap: 'anywhere'}}>{value}</span>
        },
        {
            title: 'Status',
            dataIndex: 'processed',
            sorter: true,
            width: 96,
            render: (_, record: Item) => (
                <Tag color="#D6E6FF" style={{color: '#0D1A43', textAlign: 'center'}}>
                  {record.processed ? 'Processed' : 'Pending'}
                </Tag>
            )
        },
        {
            title: 'Mode',
            key: 'mode',
            dataIndex: 'mode',
            width: 80
        },
        {
            title: 'Action',
            key: 'action',
            dataIndex: 'action',
            width: 64,
            render: (_: any, record: Item) => {
                return record.id &&
                    <Button
                        onClick={() => unlink(record.id)}
                        type="text"
                        danger
                    >
                        <DeleteOutlined/>
                    </Button>
            }
        }
    ];

    return (
        <Spin spinning={loading}>
            <Form.Item
                label="Link Filter List"
                tooltip="Use filter lists to block or allow specific domain, audiences or traffic sources for your ad group. In order to create new Filter Lists, go to the Filter Lists Page."
            >
                <Flex gap={12}>
                    <FilterListTypeSelector placeholder="Select Type" value={filterListType}
                                            onChange={(type) => changeFilterListType(type)}
                                            style={{width: 200}}
                    />
                    <FilterListModeSelector placeholder="Select Mode" value={filterListMode}
                                            onChange={(value) => setFilterListMode(value)}
                                            style={{width: 130}}
                    />
                    <Select style={{flex: 1}} placeholder="Select Filter List"
                            key={filterListSelectKey}
                            options={filterListOptions}
                            onChange={(value) => {
                            link(value)
                            setFilterListSelectKey(k => k + 1)
                            }}
                            disabled={!filterListType || !filterListMode}
                            showSearch
                    />
                </Flex>
            </Form.Item>
            {entity.filterListLinks && entity.filterListLinks.length > 0 &&
                <Row>
                    <Col span="4"></Col>
                    <Col span="16">
                        <Table className="card-table"
                               columns={filterListColumns}
                               pagination={false}
                               dataSource={filterListSelected}
                        />
                    </Col>
                </Row>
            }
        </Spin>
    );
};

export default FilterListCard;
